<template>
  <div class="ga-dashboard">
    <DomainPaneHeader :domain="domain" :loaded="true" />
    <div class="header">
      <div class="title">
        <h1><span @click="showFileUpload = !showFileUpload">📊</span> GA4 Event Dashboard</h1>
      </div>

      <div :class="['upload', { 'disabled': !showFileUpload }]">
        <div class="file-meta">{{ fileMeta }}</div>
        <div class="status" :class="{ error: statusError }">{{ statusText }}</div>
        <div class="file-wrap">
          <button class="btn">📂 CSV 파일 선택</button>
          <input
          type="file"
          ref="csvFile"
          accept=".csv"
          @change="handleFileChange"
          />
        </div>
        <button class="btn" v-show="fileMeta" @click="resetUI">
          <i class="icon icon--reset"></i>
        </button>
      </div>
    </div>

    <div class="top-split">
      <div class="top-left">
        <div class="top-left-stack">
          <div class="kpi-grid">
            <div class="card kpi-card" v-for="kpi in Object.keys(kpiList)" :key="kpi">
              <div class="kpi">
                <div class="label">{{ kpiList[kpi].label }}</div>
                <div class="value">{{ kpiList[kpi].value }}</div>
              </div>
            </div>
          </div>

          <div class="card trend-card">
            <div class="card-header">
              <div class="card-title">📅 Daily Trend</div>
              <div class="badge">Events + Revenue</div>
            </div>
            <div class="card-body">
              <div v-if="!dailyChartOptions" class="state">
                <b>CSV 파일을 업로드</b>하면 전체 기간 트렌드를 표시합니다.
              </div>
              <div v-else id="trendWrap">
                <app-chart
                  chartType="line"
                  :chartOptions="dailyChartOptions"
                  :height="158"
                  :useResize="true"
                >
                </app-chart>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="top-right">
        <p class="box-title">
          <i class="icon icon--dashboard-mine-small"></i>
          <span>AI Custom Dashboard</span>
        </p>
        <i class="icon icon--gradient-blue"></i>
        <dashboard-ai :category="'dashboardGa'" :dashboard-data="{events: events}" :no-data="!computedStats" />
      </div>
    </div>

    <div class="grid">
      <div class="card col-6">
        <div class="card-header">
          <div class="card-title">📝 Events</div>
          <div class="badge">{{ this.eventsList.length }} types</div>
        </div>

        <div class="section">
          <div class="section-head">
            <div class="h">Summary (Top 10)</div>
            <div class="t">가장 많이 발생한 이벤트</div>
          </div>
          <div v-if="!computedStats" class="state">업로드 후 요약 차트가 표시됩니다.</div>
          <div v-else class="h220">
            <app-chart
              chartType="bar"
              :chartOptions="eventsChartOptions"
              :height="220"
              :useResize="true"
            >
            </app-chart>
          </div>
        </div>

        <div class="section">
          <div class="section-head">
            <div class="h">Detail (All)</div>
            <div class="t">전체 이벤트 목록</div>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style="width:70%">event_name</th>
                  <th class="num" style="width:30%">count</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!this.eventsList.length">
                  <td colspan="2" style="padding:14px;color:var(--muted);">데이터가 없습니다.</td>
                </tr>
                <tr v-else v-for="(item, index) in eventsList" :key="index">
                  <td>{{ item.name }}</td>
                  <td class="num"><strong>{{ item.count.toLocaleString() }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="card col-6">
        <div class="card-header">
          <div class="card-title">📱 Device</div>
          <div class="badge">{{ deviceList.length }} combos</div>
        </div>

        <div class="section">
          <div class="section-head">
            <div class="h">Summary (Grouped)</div>
            <div class="t">device.category 기준</div>
          </div>
          <div v-if="!computedStats" class="state">업로드 후 디바이스 그룹 요약 차트가 표시됩니다.</div>
          <div v-else class="h220">
            <app-chart
              chartType="bar"
              :chartOptions="deviceChartOptions"
              :height="220"
              :useResize="true"
            >
            </app-chart>
          </div>
        </div>

        <div class="section">
          <div class="section-head">
            <div class="h">Detail (Device / Browser)</div>
            <div class="t">'unk / -'는 기기정보 누락</div>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style="width:70%">device / browser</th>
                  <th class="num" style="width:30%">count</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!deviceList.length">
                  <td colspan="2" style="padding:14px;color:var(--muted);">데이터가 없습니다.</td>
                </tr>
                <tr v-else v-for="(item, index) in deviceList" :key="index">
                  <td>
                    <span class="inline-tag">
                      <span class="txt">{{ item.name }}</span>
                      <span v-if="item.name === 'unk / -'" class="mini-badge">기기정보 누락</span>
                    </span>
                  </td>
                  <td class="num"><strong>{{ item.count.toLocaleString() }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="grid">
      <div class="card col-12">
        <div class="card-header">
          <div class="card-title">🔗 Traffic Medium</div>
          <div class="badge">Others {{ trafficRest.length }}</div>
        </div>

        <div class="traffic-split">
          <div class="traffic-left">
            <div class="section-head" style="margin-bottom:10px;">
              <div class="h">Summary (Top 10 + Others)</div>
              <div class="t">요약</div>
            </div>
            <div v-if="!computedStats" class="state">업로드 후 유입 구조 요약이 표시됩니다.</div>
            <div v-else class="h360">
              <app-chart
                chartType="pie"
                :chartOptions="trafficChartOptions"
                :height="360"
                :useResize="true"
              >
              </app-chart>
            </div>
          </div>

          <div class="traffic-right">
            <div class="section-head" style="margin-bottom:10px;">
              <div class="h">Detail (Others: 11위~끝)</div>
              <div class="t">Top10 외 전체</div>
            </div>
            <div class="table-wrap" style="max-height:380px;">
              <table>
                <thead>
                  <tr>
                    <th style="width:70%">traffic_source.medium</th>
                    <th class="num" style="width:30%">count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!trafficRest.length">
                    <td colspan="2" style="padding:14px;color:var(--muted);">데이터가 없습니다.</td>
                  </tr>
                  <tr v-else v-for="(item, index) in trafficRest" :key="index">
                    <td>{{ item[0] }}</td>
                    <td class="num"><strong>{{ item[1] }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid">
      <div class="card col-12">
        <div class="card-header">
          <div class="card-title">🧭 Ecommerce Funnel</div>
          <div class="badge">% of view_item</div>
        </div>
        <div class="card-body">
          <div v-if="!computedStats" class="state">업로드 후 퍼널 차트가 표시됩니다.</div>
          <div v-else class="h220">
            <app-chart
              chartType="bar"
              :chartOptions="funnelChartOptions"
              :height="220"
              :useResize="true"
            >
            </app-chart>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import Papa from 'papaparse'
import { format } from 'date-fns'
import { exampleKpiList, exampleComputedStats, exampleEvents } from './ga-dashboard.constants'
import DomainPaneHeader from '@/components/dashboard/DomainPaneHeader'
import DashboardAi from '@/components/dashboard/DashboardAi.vue'
import AppChart from '@/components/chart'

const initialKPI = {
  users: {
    label: 'Users',
    value: '-'
  },
  sessions: {
    label: 'Sessions',
    value: '-'
  },
  events: {
    label: 'Events',
    value: '-'
  },
  revenue: {
    label: 'Revenue (USD)',
    value: '-'
  },
  purchases: {
    label: 'Purchases',
    value: '-'
  },
  cvr: {
    label: 'Purchase CVR',
    value: '-'
  },
  arpp: {
    label: 'Avg Rev / Purchase',
    value: '-'
  },
  rows: {
    label: 'Raw Rows',
    value: '-'
  }
}

export default {
  name: 'GADashboard',
  components: {
    DomainPaneHeader,
    DashboardAi,
    AppChart
  },
  data () {
    return {
      statusText: '업로드한 파일이 없습니다.',
      statusError: false,
      fileMeta: '',

      rawRows: [],
      // events: [],
      // computedStats: null,
      // kpiList: initialKPI,
      events: exampleEvents,
      computedStats: exampleComputedStats,
      kpiList: exampleKpiList,
      TOP_N_TRAFFIC: 10,
      showFileUpload: false
    }
  },
  computed: {
    ...mapState([
      'domain'
    ]),
    gaDataPeriod () {
      if (!this.computedStats) return { start: '', end: '' }
      const dates = Object.keys(this.computedStats.byDate).sort()
      return {
        start: format(dates[0], 'YYYY-MM-DD'),
        end: format(dates[dates.length - 1], 'YYYY-MM-DD')
      }
    },
    dailyChartOptions () {
      if (!this.computedStats) return
      const dates = Object.keys(this.computedStats.byDate).sort()
      const eventSeries = dates.map(d => this.computedStats.byDate[d].events)
      const revenueSeries = dates.map(d => this.computedStats.byDate[d].revenue)

      const option = {
        color: ['#1E9FF5', '#FF6384'],
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['Events', 'Revenue (USD)'],
          top: 'auto',
          bottom: -5,
          left: 'center',
          textStyle: {
            color: '#666'
          }
        },
        grid: {
          top: '4%',
          left: 50,
          right: 50,
          bottom: '50%',
          containLabel: false
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLabel: {
            color: '#666',
            rotate: 45
          },
          splitLine: {
            show: true
          }
        },
        yAxis: [
          {
            type: 'value',
            name: 'Events',
            nameTextStyle: {
              color: '#666'
            },
            position: 'left',
            min: 0,
            alignTicks: true,
            nameLocation: 'middle',
            nameGap: 30,
            nameRotate: 90,
            axisLabel: {
              color: '#666'
            }
          },
          {
            type: 'value',
            name: 'Revenue',
            nameTextStyle: {
              color: '#666'
            },
            position: 'right',
            min: 0,
            alignTicks: true,
            nameLocation: 'middle',
            nameGap: 30,
            nameRotate: -90
          }
        ],
        series: [
          {
            name: 'Events',
            type: 'line',
            data: eventSeries,
            symbol: 'circle',
            symbolSize: 6,
            smooth: true,
            yAxisIndex: 0
          },
          {
            name: 'Revenue (USD)',
            type: 'line',
            data: revenueSeries,
            symbol: 'circle',
            symbolSize: 6,
            smooth: true,
            yAxisIndex: 1
          }
        ]
      }
      return option
    },
    trafficRest () {
      if (!this.computedStats) return []
      const sorted = this.computedStats.mediumSorted
      return sorted.slice(this.TOP_N_TRAFFIC)
    },
    trafficChartOptions () {
      const sorted = this.computedStats.mediumSorted
      const top = sorted.slice(0, this.TOP_N_TRAFFIC).filter(x => x[0] !== undefined)

      const labels = top.map(x => x[0])
      const data = top.map(x => x[1])

      if (this.trafficRest.length) {
        labels.push(`Others (${this.trafficRest.length})`)
        data.push(this.trafficRest.reduce((acc, cur) => acc + cur[1], 0))
      }

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          show: true,
          data: labels,
          orient: 'vertical',
          right: 10,
          top: 'center',
          type: 'scroll',
          itemGap: 10,
          textStyle: {
            color: '#666'
          }
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['40%', '50%'],
            data: labels.map((label, index) => ({
              name: label,
              value: data[index]
            })),
            label: {
              show: false
            },
            labelLine: {
              show: false
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }

      return option
    },
    eventsList () {
      if (!this.computedStats) return []
      return this.objToList(this.computedStats.eventCounts)
    },
    eventsChartOptions () {
      const sorted = Object.entries(this.computedStats.eventCounts).sort((a, b) => b[1] - a[1]).slice(0, 10)
      const topObj = Object.fromEntries(sorted)
      return this.barChartOptions(topObj)
    },
    deviceList () {
      if (!this.computedStats) return []
      return this.objToList(this.computedStats.deviceCombos)
    },
    deviceChartOptions () {
      return this.barChartOptions(this.computedStats.deviceGroupCounts)
    },
    funnelChartOptions () {
      const steps = ['view_item', 'add_to_cart', 'begin_checkout', 'purchase']
      const counts = steps.map(x => this.computedStats.funnel[x] || 0)
      const base = counts[0] || 1
      const rates = counts.map(c => (c / base) * 100)

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const idx = params[0].dataIndex
            return `${params[0].name}<br/>${rates[idx].toFixed(2)}% (${counts[idx].toLocaleString()} events)`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: steps,
          splitLine: {
            show: true
          },
          axisLabel: {
            color: '#666'
          },
          axisLine: {
            lineStyle: {
              color: '#ccc'
            }
          }
        },
        yAxis: {
          type: 'value',
          max: 100,
          axisLabel: {
            formatter: '{value}%',
            color: '#666'
          }
        },
        series: [
          {
            type: 'bar',
            data: rates,
            itemStyle: {
              color: 'rgb(95 179 239 / 55%)'
            },
            emphasis: {
              itemStyle: {
                color: 'rgb(95 179 239 / 80%)'
              }
            }
          }
        ]
      }
    }
  },
  mounted () {
    this.setJourneyPeriod({
      start: this.gaDataPeriod.start,
      end: this.gaDataPeriod.end
    })
  },

  methods: {
    ...mapMutations([
      'setDomain',
      'setJourneyOptions',
      'setJourneyPeriod'
    ]),
    handleFileChange (e) {
      const file = e.target.files[0]
      if (!file) return

      this.resetUI()
      this.fileMeta = `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)}MB`
      this.setStatus('CSV 파싱 중…')

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        worker: true,
        dynamicTyping: true,
        chunkSize: 1024 * 256,
        complete: (results) => {
          try {
            this.rawRows = results.data || []
            this.kpiList.rows.value = this.rawRows.length
            this.setStatus('이벤트 단위로 병합 중…')
            this.events = this.parseGA4Events(this.rawRows)
            this.setStatus('렌더링 중…')
            this.computedStats = this.computeStats(this.events)
            this.renderKPIs(this.computedStats)
            this.setJourneyPeriod({
              start: this.gaDataPeriod.start,
              end: this.gaDataPeriod.end
            })
            this.setStatus('완료')
          } catch (err) {
            console.error(err)
            this.setStatus('오류: 데이터 처리에 실패했습니다.', true)
            this.showErrorStates('데이터 처리 오류가 발생했습니다. CSV 포맷/컬럼을 확인해 주세요.')
          }
        },
        error: (err) => {
          console.error(err)
          this.setStatus('오류: CSV 파싱에 실패했습니다.', true)
          this.showErrorStates('CSV 파싱에 실패했습니다. 파일 인코딩/구분자를 확인해 주세요.')
        }
      })
    },

    setStatus (text, isError = false) {
      this.statusText = text
      this.statusError = isError
    },
    resetUI () {
      this.rawRows = []
      this.events = []
      this.computedStats = null
      this.kpiList = JSON.parse(JSON.stringify(initialKPI))
      this.fileMeta = ''
      this.setStatus('업로드한 파일이 없습니다.')
    },

    parseGA4Events (rows) {
      const map = new Map()
      let currentEventId = null

      for (const row of rows) {
        const hasEventIdentifier = row['event_name'] && row['event_timestamp']

        if (hasEventIdentifier) {
          const eventId = [
            row['event_timestamp'],
            row['user_pseudo_id'],
            row['event_name'],
            row['event_bundle_sequence_id']
          ].join('|')

          currentEventId = eventId

          if (!map.has(eventId)) {
            // Extract device info (supports JSON or flat columns)
            const deviceInfo = this.parseJsonColumn(row['device'])
            const device = row['device.category'] || (deviceInfo && deviceInfo.device && deviceInfo.device.category) || 'unk'
            const browser = row['device.web_info.browser'] || (deviceInfo && deviceInfo.device && deviceInfo.device.web_info && deviceInfo.device.web_info.browser) || '-'

            // Extract traffic source (supports JSON or flat columns)
            const trafficInfo = this.parseJsonColumn(row['traffic_source'])
            const medium = row['traffic_source.medium'] || (trafficInfo && trafficInfo.traffic_source && trafficInfo.traffic_source.medium) || '(direct/none)'

            map.set(eventId, {
              __id: eventId,
              event_date: row['event_date'],
              event_timestamp: row['event_timestamp'],
              event_name: row['event_name'],
              user_pseudo_id: row['user_pseudo_id'],
              traffic_medium: medium,
              device_category: device,
              browser: browser,
              device_detail: `${device} / ${browser}`,
              event_value_in_usd: this.safeNum(row['event_value_in_usd']),
              params: {}
            })

            // Parse JSON event_params if present
            const eventParamsJson = this.parseJsonColumn(row['event_params'])
            if (eventParamsJson && eventParamsJson.event_params) {
              const event = map.get(eventId)
              for (const param of eventParamsJson.event_params) {
                const paramKey = param.key
                const paramValue = param.value
                if (paramKey && paramValue) {
                  const value = paramValue.string_value || paramValue.int_value || paramValue.float_value || paramValue.double_value
                  if (value !== null && value !== undefined && value !== '') {
                    event.params[paramKey] = value
                  }
                }
              }
            }
          } else {
            const event = map.get(eventId)
            event.event_value_in_usd = Math.max(event.event_value_in_usd, this.safeNum(row['event_value_in_usd']))
          }
        }

        if (!currentEventId) continue
        const event = map.get(currentEventId)
        if (!event) continue

        // Handle flat column formats (UNNEST'd or short keys)
        const key = row['event_params.key'] || row['key']
        if (key) {
          let value = row['event_params.value.string_value'] || row['string_value']
          if (value === null || value === undefined || value === '') value = row['event_params.value.int_value'] || row['int_value']
          if (value === null || value === undefined || value === '') value = row['event_params.value.float_value'] || row['float_value']
          if (value === null || value === undefined || value === '') value = row['event_params.value.double_value'] || row['double_value']
          if (value !== null && value !== undefined && value !== '') {
            if (event.params[key] === undefined) event.params[key] = value
            else if (Array.isArray(event.params[key])) event.params[key].push(value)
            else event.params[key] = [event.params[key], value]
          }
        }

        // Handle JSON value column (last column format)
        if (row['key'] && row['value']) {
          const valJson = this.parseJsonColumn(row['value'])
          if (valJson && valJson.value) {
            const val = valJson.value.string_value || valJson.value.int_value || valJson.value.float_value || valJson.value.double_value
            if (val !== null && val !== undefined && val !== '') {
              event.params[row['key']] = val
            }
          }
        }

        if (event.ga_session_id === undefined) {
          const sessionId = event.params['ga_session_id']
          if (sessionId !== undefined) event.ga_session_id = Array.isArray(sessionId) ? sessionId[0] : sessionId
        }
      }

      return Array.from(map.values())
    },

    parseJsonColumn (val) {
      if (!val || typeof val !== 'string') return null
      try {
        return JSON.parse(val)
      } catch (e) {
        return null
      }
    },

    safeNum (v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },

    computeStats (events) {
      const users = new Set()
      const sessions = new Set()

      const byDate = {}
      const eventCounts = {}
      const deviceGroupCounts = {}
      const deviceCombos = {}
      const mediumCounts = {}

      let purchases = 0
      let revenue = 0

      const funnel = { view_item: 0, add_to_cart: 0, begin_checkout: 0, purchase: 0 }

      for (const event of events) {
        if (event.user_pseudo_id) users.add(event.user_pseudo_id)
        if (event.ga_session_id !== undefined && event.ga_session_id !== null) sessions.add(String(event.ga_session_id))

        eventCounts[event.event_name] = (eventCounts[event.event_name] || 0) + 1
        mediumCounts[event.traffic_medium] = (mediumCounts[event.traffic_medium] || 0) + 1
        deviceGroupCounts[event.device_category] = (deviceGroupCounts[event.device_category] || 0) + 1
        deviceCombos[event.device_detail] = (deviceCombos[event.device_detail] || 0) + 1

        const date = event.event_date || '(no_date)'
        if (!byDate[date]) byDate[date] = { events: 0, revenue: 0 }
        byDate[date].events += 1

        if (event.event_name in funnel) funnel[event.event_name]++

        if (event.event_name === 'purchase') {
          purchases++
          revenue += event.event_value_in_usd || 0
          byDate[date].revenue += event.event_value_in_usd || 0
        }
      }
      const mediumSorted = Object.entries(mediumCounts).sort((a, b) => b[1] - a[1])

      return { users, sessions, purchases, revenue, byDate, eventCounts, deviceGroupCounts, deviceCombos, mediumSorted, funnel }
    },

    renderKPIs (s) {
      this.kpiList.users.value = s.users.size
      this.kpiList.sessions.value = s.sessions.size
      this.kpiList.events.value = this.events.length
      this.kpiList.revenue.value = '$' + s.revenue.toFixed(2)
      this.kpiList.purchases.value = s.purchases

      const cvr = s.sessions.size ? s.purchases / s.sessions.size : 0
      this.kpiList.cvr.value = (cvr * 100).toFixed(2) + '%'

      const arpp = s.purchases ? s.revenue / s.purchases : 0
      this.kpiList.arpp.value = '$' + arpp.toFixed(2)
    },

    objToList (obj) {
      if (!obj) return []
      return Object.entries(obj)
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count }))
    },

    barChartOptions (obj) {
      const sorted = Object.entries(obj).sort((a, b) => b[1] - a[1])
      const labels = sorted.map(x => x[0])
      const data = sorted.map(x => x[1])

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          top: '4%',
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          splitLine: {
            show: true
          },
          axisLabel: {
            color: '#666'
          }
        },
        yAxis: {
          type: 'category',
          data: labels,
          inverse: true,
          splitLine: {
            show: true
          },
          axisLabel: {
            color: '#666'
          }
        },
        series: [
          {
            type: 'bar',
            data: data,
            itemStyle: {
              color: 'rgb(95 179 239 / 55%)'
            },
            emphasis: {
              itemStyle: {
                color: 'rgb(95 179 239 / 80%)'
              }
            }
          }
        ]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ga-dashboard {
  --card: #ffffff;
  --border: #e7e8ee;
  --text: #1f2330;
  --muted: #6b7280;
  --shadow: 0 10px 30px rgba(18, 23, 38, 0.06);
  --radius: 16px;
  --pad: 16px;
  --kpi-h: 78px;
  --kpi-gap: 10px;
  --trend-h: 240px;

  * {
    box-sizing: border-box;
  }

  margin: 0;
  color: var(--text);
  line-height: 1.35;

  .chart-container {
    width: 100%;
    height: 100%;
  }

  /* Header */
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    height: 72px;

    .title {
      h1 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        letter-spacing: -0.2px;
      }

      .sub {
        margin-top: 6px;
        font-size: 13px;
        color: var(--muted);
      }
    }

    .status {
      font-size: 12px;
      color: var(--muted);

      &.error {
        color: #b42318;
      }
    }
  }

  .upload {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
    min-width: 240px;
    &.disabled {
      pointer-events: none;
      .file-meta, .status {
        display: none;
      }
      opacity: 0.5;
    }
  }

  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 43px;
    min-width: 43px;
    border: 1px solid #cfd3df;
    background: #fff;
    padding: 10px 12px;
    border-radius: 12px;
    cursor: pointer;
    font-weight: 700;
    font-size: 13px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);

    &:active {
      transform: translateY(1px);
    }
  }

  .file-wrap {
    position: relative;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    input {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
    }
  }

  .file-meta {
    font-size: 12px;
    color: var(--muted);
    max-width: 280px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Layout */
  .grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .col-3 { grid-column: span 3; }
  .col-4 { grid-column: span 4; }
  .col-6 { grid-column: span 6; }
  .col-8 { grid-column: span 8; }
  .col-12 { grid-column: span 12; }

  .card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .card-header {
    padding: 12px 14px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: linear-gradient(#fff, #fbfbff);
  }

  .card-title {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: -0.2px;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .badge {
    font-size: 12px;
    color: var(--muted);
    background: #f1f3f9;
    border: 1px solid #e8eaf2;
    padding: 2px 8px;
    border-radius: 999px;
    white-space: nowrap;
  }

  .card-body {
    padding: var(--pad);
    flex: 1;
    min-height: 0;
  }

  /* TOP split layout */
  .top-split {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 16px;
    margin-bottom: 16px;
    align-items: stretch;
  }

  .top-left {
    grid-column: span 8;
    min-width: 0;
  }

  .top-right {
    grid-column: span 4;
    min-width: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    padding: 24px 25px 16px 24px;
    border-radius: 10px;
    background-color: #fff;
    box-sizing: border-box;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.32);

    position: relative;
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
    background: linear-gradient(125deg, #253CCD 0%, #1760EC 49.04%, #0074FF 100%);

    .box-title {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #fff;
      font-size: 14px;
      font-weight: 600;
    }
    .icon--gradient-blue {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .top-left-stack {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    min-height: 0;
  }

  /* KPI grid */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--kpi-gap);
  }

  .kpi-card {
    height: var(--kpi-h);
  }

  .kpi {
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    height: 100%;

    .label {
      font-size: 12px;
      color: var(--muted);
      letter-spacing: 0.2px;
    }

    .value {
      font-size: 20px;
      font-weight: 900;
      letter-spacing: -0.3px;
      line-height: 1.1;
    }
  }

  /* Daily Trend */
  .trend-card {
    height: var(--trend-h);

    .card-body {
      display: flex;
      flex-direction: column;
      min-height: 0;

      #trendWrap {
        flex: 1;
        min-height: 0;
      }
    }
  }

  /* Charts */
  .h220 { height: 220px; }
  .h360 { height: 360px; }

  /* Section inside card */
  .section {
    padding: 14px;
    border-top: 1px solid var(--border);

    &:first-child {
      border-top: none;
    }
  }

  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;

    .h {
      font-size: 13px;
      font-weight: 800;
      color: var(--text);
    }

    .t {
      font-size: 12px;
      color: var(--muted);
      white-space: nowrap;
    }
  }

  /* Tables */
  .table-wrap {
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: auto;
    max-height: 320px;
    background: #fff;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead th {
      position: sticky;
      top: 0;
      background: #fff;
      z-index: 1;
      border-bottom: 1px solid var(--border);
      font-weight: 800;
      color: #4b5563;
      padding: 10px 12px;
      text-align: left;
    }

    tbody td {
      padding: 10px 12px;
      border-bottom: 1px solid #f0f1f6;
      vertical-align: top;
    }

    tbody tr {
      &:nth-child(2n) {
        background: #fafbff;
      }

      &:hover {
        background: #f3f6ff;
      }
    }
  }

  .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  /* Inline tag */
  .inline-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
    vertical-align: middle;
    white-space: nowrap;

    .txt {
      white-space: nowrap;
    }

    .mini-badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: 999px;
      font-size: 12px;
      border: 1px solid #f2d3a7;
      background: #fff7ea;
      color: #8a5200;
      white-space: nowrap;
      line-height: 1.2;
      transform: translateY(-1px);
    }
  }

  /* Empty / State */
  .state {
    border: 1px dashed #d7dbe8;
    border-radius: 14px;
    padding: 14px;
    color: var(--muted);
    background: #fcfcff;
    font-size: 13px;

    b {
      color: #374151;
    }
  }

  .error {
    border-color: #f3c1c1;
    background: #fff7f7;
    color: #b42318;
  }

  /* Traffic split layout */
  .traffic-split {
    display: grid;
    grid-template-columns: 1.8fr 1fr;
    gap: 16px;
    padding: 14px;
  }

  .traffic-left,
  .traffic-right {
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 14px;
    background: #fff;
    min-width: 0;
  }

  /deep/ .dashboard-ai {
    .editor-wrap {
      .icon-wrap {
        top: 100px;
      }
    }
  }
}
</style>
